import { memo, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Accordion } from '@mui/material'
import PropTypes from 'prop-types'

import { useGetOrderItemsQuery } from '@/app/services/orderItems'
import { useCalculateOrderMutation, useGetOrderQuery } from '@/app/services/orders'
import {
    addCollapsedItem,
    removeCollapsedItem,
    selectCollapsedItems,
    selectFilterOptions,
} from '@/app/slices/orderItemsSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import {
    orderItemErrors,
    orderItemHasIssues,
    orderItemUnset,
    orderItemWarnings,
} from '@/common/utils/OrderUtils/OrderUtilities'

import ItemDetails from './ItemDetails'
import ItemSummary from './ItemSummary'

const classes = {
    AccordionRoot: {
        overflow: 'hidden',
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.grey[400]}`,
        backgroundColor: 'background.default',
        '&:before': {
            display: 'none',
        },
        containerType: 'inline-size',
        containerName: 'OrderItem',
    },

    AccordionDetailsRoot: {
        backgroundColor: 'background.paper',
    },
}

const OrderItem = ({ id }) => {
    const dispatch = useDispatch()
    const { orderId } = useParams()
    const organisationId = useSelector(selectOrganisationId)
    const collapsedItemsArray = useSelector(selectCollapsedItems)
    const filterOptions = useSelector(selectFilterOptions)

    const [, { isLoading: isCalculating }] = useCalculateOrderMutation({
        fixedCacheKey: 'shared-calculate-order',
    })
    const { isFetching: isFetchingOrder } = useGetOrderQuery({
        organisationId,
        orderId,
    })

    const { attributes, isDragging, listeners, setActivatorNodeRef, setNodeRef, transform, transition } = useSortable({
        id: id,
        disabled: isCalculating || isFetchingOrder || filterOptions !== 'showAllItems',
    })

    const [isCollapsed, setIsCollapsed] = useState(collapsedItemsArray.includes(id))

    const { orderItem } = useGetOrderItemsQuery(
        {
            organisationId,
            orderId,
        },
        {
            selectFromResult: ({ data }) => ({
                orderItem: data?.find((item) => item.id === id),
            }),
        }
    )

    const itemHasIssues = useMemo(() => orderItemHasIssues(orderItem), [orderItem])
    const itemWarnings = useMemo(() => orderItemWarnings(orderItem), [orderItem])
    const itemErrors = useMemo(() => orderItemErrors(orderItem), [orderItem])
    const itemIsUnset = useMemo(() => orderItemUnset(orderItem), [orderItem])

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
        zIndex: isDragging ? 0 : undefined,
    }

    const handleCollapsedState = () => {
        setIsCollapsed(!isCollapsed)
        if (isCollapsed) {
            dispatch(removeCollapsedItem(id))
        } else {
            dispatch(addCollapsedItem(id))
        }
    }

    const isRotary = useMemo(() => {
        if (!orderItem) return false
        return 'profile' in orderItem && orderItem?.profile !== ''
    }, [orderItem])

    useEffect(() => {
        setIsCollapsed(collapsedItemsArray.includes(id))
    }, [collapsedItemsArray, id])

    if (orderItem)
        return (
            <Accordion
                expanded={!isCollapsed}
                ref={setNodeRef}
                style={style}
                sx={classes.AccordionRoot}
                variant="outlined"
                square
                {...attributes}
                onChange={() => handleCollapsedState()}
            >
                <ItemSummary
                    hasErrors={itemErrors}
                    hasIssues={itemHasIssues}
                    hasWarnings={itemWarnings}
                    isUnset={itemIsUnset}
                    listeners={listeners}
                    orderItemId={id}
                    setActivatorNodeRef={setActivatorNodeRef}
                />

                <ItemDetails
                    isRotary={isRotary}
                    orderItemId={id}
                />
            </Accordion>
        )
}

OrderItem.propTypes = {
    id: PropTypes.string,
}

export default memo(OrderItem)
